import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { getDealerPanelModelQuestions } from 'services/ModelService';
import {
  getModelDocumentVisibility,
  submitVisibilityConfiguration,
} from 'services/VisibilityConfigurationService';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { IDocVisibilityOptionEnum } from './constants';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';

export interface IVisibilityConfigurationDTO {
  values: object;
  modelId: number;
  base64Metadata?: string;
}
export const useSubmitVisibilityConfiguration = () => {
  const { t } = useTranslation();
  return useMutation(
    (visibilityConfigurationDTO: IVisibilityConfigurationDTO) => {
      return submitVisibilityConfiguration(visibilityConfigurationDTO);
    },
    {
      onSuccess: () => {
        toast.success(t('Applied document visibility configuration'));
      },
      onError: () => {
        toast.error(
          t('Error while applying document visibility configuration')
        );
      },
      mutationKey: ReactMutationKeys.SUBMIT_VISIBILITY_CONFIGURATION,
    }
  );
};

export const useGetDealerPanelModelQuestions = () => {
  const { t } = useTranslation();
  return useMutation(
    (modelId: number) => {
      return getDealerPanelModelQuestions(modelId);
    },
    {
      onError: () => {
        toast.error(t('Failed to obtain model questions'));
      },
      mutationKey: ReactMutationKeys.GET_DEALER_PANEL_MODEL_QUESTIONS,
    }
  );
};

export const useGetDocumentVisibilityOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(
    () => [
      {
        label: t('Draft quotation'),
        value: IDocVisibilityOptionEnum.DRAFT_QUOTATION,
      },
      {
        label: t('Definitive quotation'),
        value: IDocVisibilityOptionEnum.DEFINITIVE_QUOTATION,
      },
      { label: t('Sales order'), value: IDocVisibilityOptionEnum.SALES_ORDER },
      {
        label: t('Purchase order'),
        value: IDocVisibilityOptionEnum.PURCHASE_ORDER,
      },
      {
        label: t('Sales invoice'),
        value: IDocVisibilityOptionEnum.SALES_INVOICE,
      },
    ],
    [t, i18n.language]
  );
};

export const useGetModelDocumentVisibility = () => {
  const base64Metadata: string | undefined = useSelector(
    (state: IRootReducerState) => state.submitConfigurationInfo.base64Metadata
  );
  const { t } = useTranslation();
  return useMutation(
    (modelId: number) => {
      return getModelDocumentVisibility(modelId, base64Metadata);
    },
    {
      onError: () => {
        toast.error(t('Failed to obtain document visibility options'));
      },
      mutationKey: ReactMutationKeys.GET_MODEL_DOCUMENT_VISIBILITY,
    }
  );
};
